import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Inscription, List, Preloader, Text, useResponsive, WarningCard, WarningIcon } from '@common-fe/common-fe';

import AppButton from '@/components/controls/AppButton';

import { CardPurseAccess } from '../ManageAccountAccessModal.types';

import { AccountAccessListItem } from './AccountAccessListItem';

interface Props {
  purses: CardPurseAccess[];
  lockedIds: string[];
  isLoading?: boolean;
  onToggle: (id: string, locked: boolean) => void;
  onCancel: () => void;
  onSave: () => void;
}

export const ManageAccountAccessForm: React.FC<Props> = ({
  purses,
  lockedIds,
  isLoading,
  onToggle,
  onCancel,
  onSave,
}) => {
  const { t } = useTranslation();
  const { isMobile } = useResponsive();

  return (
    <Box direction="column" data-testid="manage-account-access-form">
      <Box
        round="moduleRound"
        pad={isMobile ? undefined : 'spacing24'}
        background="module"
      >
        <Text color="textBody" weight="bold" margin={{ bottom: isMobile ? 'spacing8' : 'spacing16' }}>
          {t(`Accounts: ${purses.length}`)}
        </Text>
        {!isMobile && (
          <List>
            <List.Header pad={{ vertical: 'spacing6', left: 'spacing24', right: '81px' }}>
              <Text color="textBody" size="small" weight="bold">{t('Accounts')}</Text>
              <Box margin={{ left: 'auto' }}>
                <Text color="textBody" size="small" weight="bold">{t('Access')}</Text>
              </Box>
            </List.Header>
          </List>
        )}
        <Box>
          {purses.map((purse) => (
            <AccountAccessListItem
              key={purse.id}
              purse={purse}
              isLocked={lockedIds.includes(purse.id)}
              onToggle={onToggle}
            />
          ))}
        </Box>
        <Box margin={{ top: 'spacing16' }}>
          <WarningCard
            icon={<WarningIcon />}
            title={(
              <Inscription weight="bold" lineHeight="20px" color="textBody">
                {t('The account(s) selected will be locked for the primary card as well as any dependent cards.')}
              </Inscription>
            )}
            topBorderColor="transparent"
            containerProps={{
              pad: { horizontal: 'spacing24', vertical: 'spacing16' },
              round: 'container2Round',
            }}
          />
        </Box>
      </Box>
      {!isMobile && (
        <Box
          direction="row"
          fill="horizontal"
          margin={{ top: 'spacing24' }}
          align="center"
          justify="end"
          gap="spacing12"
        >
          <AppButton
            buttonType="secondary"
            width="140px"
            type="button"
            testId="manage-account-access-form_cancel-button"
            onClick={onCancel}
          >
            {t('Cancel')}
          </AppButton>
          <AppButton
            disabled={isLoading}
            type="button"
            width="140px"
            testId="manage-account-access-form_save-button"
            onClick={onSave}
          >
            {isLoading ? <Preloader color="white" /> : t('Save')}
          </AppButton>
        </Box>
      )}
    </Box>
  );
};
